import * as React from 'react'
import { useMemo } from 'react'
import { AccountDto } from '@halo/dtos/account.dto'
import { Button, Drawer, StatusBadge, Well } from '@pushly/aqe/lib/components'
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Divider, Flex, Form, Input, notification, Radio, Select, Skeleton, Space, Switch, Typography } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import { simpleNotification } from '@halo/_utils/notifications'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { PromptReferrerAllowType } from '@halo/enums/prompt-referrer-allow-type'
import { isDef } from '@pushly/aqe/lib/utils/is-def'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { AccountService } from '@halo/services/account.service'
import { Container } from 'typescript-ioc/es5'
import { TypedTagSelect } from '@halo/components/typed-tag-select/typed-tag-select'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import cloneDeep from 'clone-deep'
import deepEqual from 'fast-deep-equal'
import * as randomstring from 'randomstring'
import {
    calculateToPromptFcapSeconds,
    getFcapAndReferrerConfigs,
    prepareGlobalPromptSettings,
} from '@halo/_utils/fcap-and-referrer-configs'

interface IAccountPromptEditor {
    account: AccountDto
    visible: boolean
    onClose: (e: any) => void
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

export const AccountPromptEditor: React.FC<IAccountPromptEditor> = (props) => {
    const accountService = useMemo(() => Container.get(AccountService), [])
    // Retained singletons for parity with the previous class (some consumers may rely on side effects).
    useMemo(() => Container.get(AppState), [])
    useMemo(() => Container.get(AppService), [])

    const [form] = Form.useForm()
    // Re-render on any field change so conditional UI (fcapsEnabled / referrerConditionsEnabled) updates.
    Form.useWatch([], form)

    const { account } = props
    const config = getFcapAndReferrerConfigs(account)

    const fcapsEnabled = form.getFieldValue('fcapsEnabled') ?? !!account.frequency_caps?.prompt
    const referrerConditionsEnabled =
        form.getFieldValue('referrerConditionsEnabled') ?? account.global_prompt_settings?.referrers?.enabled ?? false

    const getCurrentValues = (): Partial<AccountDto> => {
        const values: Partial<AccountDto> = {}
        const formValues = form.getFieldsValue()

        if (formValues.fcapsEnabled) {
            values.frequency_caps = {
                prompt: {
                    occurrences: formValues.promptFcapOccurrences,
                    fcap_seconds: calculateToPromptFcapSeconds(formValues),
                    display_metric: formValues.promptFcapDisplayMetric,
                },
            }
        } else {
            values.frequency_caps = null
        }
        if (deepEqual(values.frequency_caps, account.frequency_caps)) {
            delete values.frequency_caps
        }

        const globalPromptSettings = prepareGlobalPromptSettings(formValues, cloneDeep(account.global_prompt_settings))
        if (!deepEqual(globalPromptSettings, account.global_prompt_settings)) {
            values.global_prompt_settings = globalPromptSettings
        }

        return values
    }

    const handleSubmit = async () => {
        const mergedValues = getCurrentValues()
        const { ok, data } = await accountService.patch(account.id, mergedValues, true)
        if (ok) {
            props.onAccountUpdated(data)
            props.onClose(null)
        }
    }

    const onDrawerSubmit = async () => {
        try {
            await form.validateFields()
            await handleSubmit()
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    const currentValues = getCurrentValues()

    return (
        <Drawer
            className={classnames('adf-prompt-settings-editor')}
            title="Update Details"
            placement="right"
            closable={true}
            width={800}
            onSubmit={onDrawerSubmit}
            disableSubmit={Object.keys(currentValues).length === 0}
            onClose={props.onClose}
            open={props.visible}
        >
            <Form
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                form={form}
                onFinish={handleSubmit}
                size="small"
                labelAlign="left"
                requiredMark={false}
            >
                <Well title="Frequency Caps" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item
                        label="Prompt Frequency Cap"
                        name="fcapsEnabled"
                        valuePropName="checked"
                        initialValue={!!account.frequency_caps?.prompt}
                    >
                        <Switch aria-label="Enable Prompt Frequency Cap" />
                    </Form.Item>

                    {fcapsEnabled && (
                        <Form.Item>
                            <Flex vertical gap={8}>
                                <Space size="small">
                                    <Typography.Text>A visitor to my site should be prompted</Typography.Text>
                                    <Space.Compact>
                                        <Form.Item
                                            name="promptFcapOccurrences"
                                            initialValue={config.defaultPromptFcapOccurrences}
                                            rules={[
                                                {
                                                    pattern: /^[\d]+$/i,
                                                    message: 'Occurrence count must be numeric',
                                                },
                                                {
                                                    validator: async (_rule, value) => {
                                                        if (!(value >= 1)) {
                                                            throw new Error(
                                                                'Occurrence count must be 1 or greater',
                                                            )
                                                        }
                                                    },
                                                },
                                            ]}
                                            noStyle
                                        >
                                            <Input style={{ width: 60 }} placeholder="1" autoComplete="off" />
                                        </Form.Item>
                                        <Space.Addon>time(s) every</Space.Addon>
                                    </Space.Compact>
                                    <Space.Compact>
                                        <Form.Item
                                            name="promptFcap"
                                            initialValue={config.defaultPromptFcapFrequency}
                                            rules={[
                                                {
                                                    pattern: /^[\d]+$/i,
                                                    message: 'Frequency must be numeric',
                                                },
                                                {
                                                    validator: async (_rule, value) => {
                                                        if (!(value >= 1 && value <= 30)) {
                                                            throw new Error(
                                                                'Frequency must be 1 or greater and less than or equal to 30',
                                                            )
                                                        }
                                                    },
                                                },
                                            ]}
                                            noStyle
                                        >
                                            <Input style={{ width: 60 }} placeholder="1" autoComplete="off" />
                                        </Form.Item>
                                        <Form.Item
                                            name="promptFcapDisplayMetric"
                                            initialValue={config.defaultPromptFcapDisplayMetric}
                                            noStyle
                                        >
                                            <Select
                                                style={{ width: 100 }}
                                                options={[
                                                    { value: 'days', label: 'day(s)' },
                                                    { value: 'hours', label: 'hour(s)' },
                                                    { value: 'minutes', label: 'minute(s)' },
                                                ]}
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                </Space>
                            </Flex>
                        </Form.Item>
                    )}

                    <Space align="start">
                        <InfoCircleOutlined className="info-icon" />
                        <Typography.Paragraph
                            type="secondary"
                            className="font-size--s"
                            style={{ marginBottom: 0 }}
                        >
                            The frequency cap will determine how often a user that does not interact (dismiss or
                            allow) with a prompt will be prompted again to subscribe.
                        </Typography.Paragraph>
                    </Space>
                </Well>

                <Divider />

                <Well title="Referrer Conditions" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item
                        label="Prompt Referrer Conditions"
                        name="referrerConditionsEnabled"
                        valuePropName="checked"
                        initialValue={account.global_prompt_settings?.referrers?.enabled ?? false}
                    >
                        <Switch aria-label="Enable Prompt Referrer Conditions" />
                    </Form.Item>

                    {referrerConditionsEnabled && (
                        <Flex vertical gap={16}>
                            <Form.Item
                                label="When referrer is blank?"
                                name="referrerWhenBlank"
                                initialValue={config.defaultReferrerWhenBlank}
                            >
                                <Radio.Group
                                    optionType="button"
                                    buttonStyle="solid"
                                    options={[
                                        {
                                            value: PromptReferrerAllowType.IGNORE,
                                            label: PromptReferrerAllowType.IGNORE,
                                        },
                                        {
                                            value: PromptReferrerAllowType.ALLOW,
                                            label: PromptReferrerAllowType.ALLOW,
                                        },
                                        {
                                            value: PromptReferrerAllowType.BLOCK,
                                            label: PromptReferrerAllowType.BLOCK,
                                        },
                                    ]}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Allowed Referrers"
                                name="referrerAllow"
                                initialValue={config.defaultReferrerAllowValue}
                            >
                                <TypedTagSelect types={['exact', 'contains']} />
                            </Form.Item>

                            <Form.Item
                                label="Blocked Referrers"
                                name="referrerBlock"
                                initialValue={config.defaultReferrerBlockValue}
                            >
                                <TypedTagSelect types={['exact', 'contains']} />
                            </Form.Item>
                        </Flex>
                    )}

                    <Space align="start">
                        <InfoCircleOutlined className="info-icon" />
                        <Typography.Paragraph
                            type="secondary"
                            className="font-size--s"
                            style={{ marginBottom: 0 }}
                        >
                            Referrer conditions determine if a user is able to see a prompt based on where they
                            arrived from.
                        </Typography.Paragraph>
                    </Space>
                </Well>
            </Form>
        </Drawer>
    )
}

interface IAccountPromptWell {
    loading?: boolean
    account: AccountDto
    title?: string
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

interface IAPWState {
    showDrawer: boolean
    drawerKey?: string
}

export class AccountPromptWell extends React.Component<IAccountPromptWell, IAPWState> {
    public state: IAPWState = {
        showDrawer: false,
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Prompt Settings"
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderFrequencyCaps()}
                            {this.renderReferrerConditions()}

                            <AccountPromptEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                account={this.props.account}
                                onClose={this.handleDrawerClose}
                                onAccountUpdated={this.props.onAccountUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderFrequencyCaps() {
        const enabled = !!this.props.account.frequency_caps?.prompt
        const config = getFcapAndReferrerConfigs(this.props.account)

        return (
            <Well.Section title="Frequency Caps">
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>
                        <StatusBadge type="generic" expanded={true} status={enabled ? 'ENABLED' : 'DISABLED'} />
                    </div>
                </div>

                {enabled && (
                    <>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Prompt visitors to my site</div>
                            <div className={classnames('form-layout-row-value')}>
                                {config.defaultPromptFcapOccurrences} time(s) every {config.defaultPromptFcapFrequency}{' '}
                                {config.defaultPromptFcapDisplayMetric.replace(/s$/, '(s)')}
                            </div>
                        </div>
                    </>
                )}
            </Well.Section>
        )
    }

    protected renderReferrerConditions() {
        const enabled = this.props.account.global_prompt_settings?.referrers?.enabled ?? false
        const config = getFcapAndReferrerConfigs(this.props.account)

        return (
            <Well.Section title="Referrer Conditions">
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>
                        <StatusBadge type="generic" expanded={true} status={enabled ? 'ENABLED' : 'DISABLED'} />
                    </div>
                </div>

                {enabled && (
                    <>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Blank Referrer Behavior</div>
                            <div className={classnames('form-layout-row-value')}>
                                {titleCase(config.defaultReferrerWhenBlank)}
                            </div>
                        </div>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Allowed Referrers</div>
                            <div className={classnames('form-layout-row-value')}>
                                {!isDef(config.defaultReferrerAllowValue)
                                    ? 'All Referrers'
                                    : config.defaultReferrerAllowValue.map((ref: any) => (
                                          <div key={ref.value}>
                                              <span>
                                                  {ref.type}: {ref.value}
                                              </span>
                                          </div>
                                      ))}
                            </div>
                        </div>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Blocked Referrers</div>
                            <div className={classnames('form-layout-row-value')}>
                                {!isDef(config.defaultReferrerBlockValue)
                                    ? 'No Referrers'
                                    : config.defaultReferrerBlockValue.map((ref: any) => (
                                          <div key={ref.value}>
                                              <span>
                                                  {ref.type}: {ref.value}
                                              </span>
                                          </div>
                                      ))}
                            </div>
                        </div>
                    </>
                )}
            </Well.Section>
        )
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
